import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
  ErrorHandler,
  APP_INITIALIZER,
} from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import * as Sentry from '@sentry/angular';

import { routes } from './app.routes';
import { provideToastr } from 'ngx-toastr';
import { provideSpinnerConfig } from 'ngx-spinner';
import {
  createInterceptorCondition,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  IncludeBearerTokenCondition,
  includeBearerTokenInterceptor,
  provideKeycloak,
} from 'keycloak-angular';
import { environment } from '../environments/environment';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { authInterceptor } from './interceptors/auth-interceptor';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHighlightOptions } from 'ngx-highlightjs';

const apiUrlPattern = new RegExp(`^${environment.apiUrl}(\\/.*)?$`, 'i');

// tracerUrlPattern matches tracer endpoints that need a Bearer token.
// The negative lookahead excludes the public /version and /ping
// endpoints — those are registered outside the auth-gated route group
// in main.go and don't need a token. Crucially, the bearer interceptor
// runs *before* authInterceptor in the chain (provideHttpClient
// withInterceptors below), so it can block requests at app boot while
// keycloak is still initialising. Without this exclusion, the
// Footer's /version fetch never lands and the version display gets
// stuck on the "—" placeholder.
const tracerUrlPattern = new RegExp(
  `^${environment.tracerUrl}(?!/(?:version|ping|stats)(?:[?#]|$))(?:/.*)?$`,
  'i'
);

const urlCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: apiUrlPattern,
  bearerPrefix: 'Bearer',
});

const tracerUrlCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: tracerUrlPattern,
  bearerPrefix: 'Bearer',
});

// Non-production envs use the public "agents" landing (check-sso, no
// forced redirect). 'release' (.live, production:false) was omitted,
// so it fell through to the prod branch — realm:'admins' +
// onLoad:'login-required' — and instant-redirected to the admins
// Keycloak realm instead of rendering the homepage. Include it.
const isStagingOrSandboxEnv =
  environment.name === 'staging' ||
  environment.name === 'sandbox' ||
  environment.name === 'release';

export const appConfig: ApplicationConfig = {
  providers: [
    provideKeycloak({
      config: {
        url: environment.keycloakUrl,
        realm: localStorage.getItem('agRealm') || 'agents',
        clientId: 'tracer',
      },
      initOptions: {
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
      },
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: [urlCondition, tracerUrlCondition],
    },
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([includeBearerTokenInterceptor, authInterceptor])),
    provideAnimations(),
    provideToastr({
      positionClass: 'toast-bottom-center',
      timeOut: 3000,
      closeButton: true,
      preventDuplicates: true,
    }),
    provideSpinnerConfig({
      type: 'ball-clip-rotate-multiple',
    }),
    provideHighlightOptions({
      fullLibraryLoader: () => import('highlight.js'),
    }),
  ],
};
